import { MailWarningIcon } from 'lucide-react'
import { useState, type SubmitEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { updateUser, type ManagedUser } from '@/api/users'
import { useApiErrorMessage } from '@/api/errors'
import { Role } from '@/auth/roles'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Field } from '@/components/layout/Field'
import { FieldGrid } from '@/components/layout/FormSection'
import { Input } from '@/components/ui/input'
import { RoleSelect } from '@/components/users/RoleSelect'
import { isEmail } from '@/components/users/validation'
import { bareRole } from '@/lib/userDisplay'

type Errors = { email?: string; firstName?: string; lastName?: string }

export function EditUserDialog({
  user,
  onOpenChange,
  onSaved,
}: {
  /** Null closes the dialog. Carrying the user in the prop keeps the two from drifting apart. */
  user: ManagedUser | null
  onOpenChange: (open: boolean) => void
  onSaved: () => void
}) {
  return (
    <Dialog open={user !== null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        {/* Keyed by id, so opening the dialog on a second row mounts a fresh form rather than
            copying the new values over the old ones in an effect. */}
        {user && (
          <EditForm key={user.id} user={user} onOpenChange={onOpenChange} onSaved={onSaved} />
        )}
      </DialogContent>
    </Dialog>
  )
}

function EditForm({
  user,
  onOpenChange,
  onSaved,
}: {
  user: ManagedUser
  onOpenChange: (open: boolean) => void
  onSaved: () => void
}) {
  const { t } = useTranslation()
  const errorMessage = useApiErrorMessage()

  const [email, setEmail] = useState(user.email ?? '')
  const [firstName, setFirstName] = useState(user.firstName ?? '')
  const [lastName, setLastName] = useState(user.lastName ?? '')
  const [role, setRole] = useState<Role>(bareRole(user.role) ?? Role.SALES)
  const [errors, setErrors] = useState<Errors>({})
  const [failure, setFailure] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // Case-insensitively, like the backend: Keycloak folds the address it stores, so re-saving an
  // untouched form in a different case is not a change and must not warn as if it were.
  const emailChanged = email.trim().toLowerCase() !== (user.email ?? '').trim().toLowerCase()

  function validate(): boolean {
    const next: Errors = {}
    if (!email.trim()) {
      next.email = t('validation.required')
    } else if (!isEmail(email)) {
      next.email = t('validation.email')
    }
    if (!firstName.trim()) {
      next.firstName = t('validation.required')
    }
    if (!lastName.trim()) {
      next.lastName = t('validation.required')
    }
    setErrors(next)
    return Object.keys(next).length === 0
  }

  async function submit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault()
    if (submitting || !validate()) {
      return
    }
    setSubmitting(true)
    setFailure(null)
    try {
      const address = email.trim()
      const result = await updateUser(user.id, {
        email: address,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        role,
      })
      // The backend decides what counts as a change, not this form: null means the address stayed
      // as it was and nothing needed sending, which is not the same as a mail that failed.
      if (result.verificationSent === null) {
        toast.success(t('users.edit.success'))
      } else if (result.verificationSent) {
        toast.success(t('users.edit.successWithNewEmail', { email: address }), { duration: 10_000 })
      } else {
        // The address has changed either way — the account is already on it — so this is a warning
        // about the mail, not a failure of the save.
        toast.warning(t('users.edit.successWithoutMail', { email: address }), { duration: 12_000 })
      }
      onSaved()
      onOpenChange(false)
    } catch (error) {
      // The interesting one is SELF_DEMOTE / LAST_ADMIN, and it belongs next to the role field
      // that caused it — not in a toast that outlives the dialog.
      setFailure(errorMessage(error, t('users.edit.failed')))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={submit} noValidate>
      <DialogHeader>
        <DialogTitle>{t('users.edit.title')}</DialogTitle>
        <DialogDescription>{t('users.edit.description')}</DialogDescription>
      </DialogHeader>

      <div className="grid gap-4 py-6">
        {failure && (
          <Alert variant="destructive">
            <AlertDescription>{failure}</AlertDescription>
          </Alert>
        )}

        <Field id="edit-email" label={t('users.invite.email')} error={errors.email}>
          <Input
            id="edit-email"
            type="email"
            autoComplete="off"
            value={email}
            aria-invalid={Boolean(errors.email)}
            aria-describedby={errors.email ? 'edit-email-error' : undefined}
            onChange={(event) => setEmail(event.target.value)}
          />
          {/* Shown while the field differs, not as a confirmation step: the consequence is worth
              knowing before saving, and an administrator who meant it should not have to click
              twice. Inside the field rather than after it, so it sits with the address it is
              about — and it never shows next to the error, the two are exclusive. */}
          {emailChanged && !errors.email && (
            <Alert className="text-amber-600 dark:text-amber-500">
              <MailWarningIcon />
              <AlertDescription className="text-current/90">
                {t('users.edit.emailChangeWarning')}
              </AlertDescription>
            </Alert>
          )}
        </Field>

        <FieldGrid>
          <Field id="edit-first-name" label={t('users.invite.firstName')} error={errors.firstName}>
            <Input
              id="edit-first-name"
              value={firstName}
              aria-invalid={Boolean(errors.firstName)}
              aria-describedby={errors.firstName ? 'edit-first-name-error' : undefined}
              onChange={(event) => setFirstName(event.target.value)}
            />
          </Field>
          <Field id="edit-last-name" label={t('users.invite.lastName')} error={errors.lastName}>
            <Input
              id="edit-last-name"
              value={lastName}
              aria-invalid={Boolean(errors.lastName)}
              aria-describedby={errors.lastName ? 'edit-last-name-error' : undefined}
              onChange={(event) => setLastName(event.target.value)}
            />
          </Field>
        </FieldGrid>

        <Field id="edit-role" label={t('users.invite.role')}>
          <RoleSelect id="edit-role" value={role} onChange={setRole} />
        </Field>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={submitting}>
          {t('users.edit.submit')}
        </Button>
      </DialogFooter>
    </form>
  )
}
